import { useMemo } from "react";

import { Column, Row, Select, AddBoxIcon } from "@hightouch/htui";

import { useFilterableOptionsContext } from "src/components/explore/filter-popover/filterable-options-hook";
import {
  QueryBuilderContext,
  useQueryBuilderContext,
} from "src/components/explore/context/hooks";
import { isDenied } from "src/components/explore/restrictions";
import { isRealtimeEntryEventArrayItemTypeSupported } from "src/components/explore/visual/condition-validation";
import { ErrorMessage } from "src/components/explore/visual/error-message";
import {
  createSubconditionUpdater,
  removeSubconditionMutative,
} from "src/components/explore/utils/condition-builders";
import { isAndOrCondition } from "src/components/explore/utils/type-guards";
import { splitArrayItemColumnName } from "src/pages/schema/columns/object-array-columns";
import {
  ColumnType,
  ConditionType,
  DefaultOperators,
  type FilterableColumn,
  type PropertyCondition,
  TimestampOperator,
  TimeType,
  isColumnReference,
  isRelatedJourneyEventColumn,
} from "src/types/visual";

import { DetailButton, AndOrToggleButton, XButton } from "./condition-buttons";
import type { FilterProps } from "./condition";
import { PropertyOperatorValueFilter } from "./property-operator-value-filter";
import { usePropertyFilterValidation } from "./property-filter";
import { getModelIdFromColumn, getPropertyNameFromColumn } from "./utils";

type ObjectArrayItemFiltersProps = Pick<
  FilterProps<PropertyCondition>,
  "condition" | "onUpdate" | "parent" | "isDisabled"
> & {
  /** The columns of the model that declares the array. Defaults to the query builder's filterable columns. */
  columns?: ReadonlyArray<
    Pick<FilterableColumn, "name" | "type" | "custom_type">
  >;
};

export const ObjectArrayItemFilters = ({
  columns,
  condition,
  onUpdate,
  parent,
  isDisabled,
}: ObjectArrayItemFiltersProps) => {
  const { columns: filterableColumns } = useFilterableOptionsContext();
  const context = useQueryBuilderContext();
  const isRealtimeEntryEvent =
    context.realtimeConditionScope === "journey-criteria" &&
    isRelatedJourneyEventColumn(condition.property);
  const itemContext = useMemo(
    () =>
      isRealtimeEntryEvent && !isDenied(context.restrictions, "relative-dates")
        ? {
            ...context,
            restrictions: {
              ...context.restrictions,
              denied: [
                ...(context.restrictions?.denied ?? []),
                "relative-dates" as const,
              ],
            },
          }
        : context,
    [context, isRealtimeEntryEvent],
  );
  const arrayPath = getPropertyNameFromColumn(condition.property);
  const modelId =
    (isRelatedJourneyEventColumn(condition.property)
      ? condition.property.column.modelId
      : isColumnReference(condition.property)
        ? getModelIdFromColumn(condition.property)
        : null) ?? "";
  const itemColumns = (columns ?? filterableColumns ?? []).flatMap((column) => {
    const split = splitArrayItemColumnName(column.name);
    if (!split || split.arrayPath !== arrayPath) {
      return [];
    }
    const propertyType = (column.custom_type ?? column.type) as ColumnType;
    if (
      isRealtimeEntryEvent &&
      !isRealtimeEntryEventArrayItemTypeSupported(propertyType)
    ) {
      return [];
    }
    return [
      {
        relativePath: split.relativePath,
        propertyType,
      },
    ];
  });
  const group = condition.subconditions?.[0];
  const predicates = isAndOrCondition(group) ? group.conditions : [];
  const groupType = isAndOrCondition(group) ? group.type : ConditionType.And;
  const generateUpdater = createSubconditionUpdater(onUpdate);

  const addPredicate = () => {
    const field = itemColumns[0];
    if (!field) {
      return;
    }
    onUpdate((current) => {
      const existing = current.subconditions?.[0];
      const top = isAndOrCondition(existing)
        ? existing
        : {
            id: crypto.randomUUID(),
            type: ConditionType.And as const,
            conditions: [],
          };
      current.subconditions = [
        {
          ...top,
          conditions: [
            ...top.conditions,
            {
              id: crypto.randomUUID(),
              type: ConditionType.Property,
              property: {
                type: "raw",
                name: field.relativePath,
                modelId,
              },
              propertyType: field.propertyType,
              operator:
                isRealtimeEntryEvent &&
                field.propertyType === ColumnType.Timestamp
                  ? TimestampOperator.Before
                  : DefaultOperators[field.propertyType],
              ...(isRealtimeEntryEvent &&
              field.propertyType === ColumnType.Timestamp
                ? { timeType: TimeType.Absolute }
                : {}),
              value: null,
            },
          ],
        },
      ];
    });
  };

  return (
    <QueryBuilderContext.Provider value={itemContext}>
      <Column gap={2} pl={8}>
        {predicates.map((predicate, index) => (
          <ItemPredicate
            key={predicate.id ?? index}
            condition={predicate}
            isDisabled={isDisabled}
            isRealtimeEntryEvent={isRealtimeEntryEvent}
            isFirst={index === 0}
            groupType={groupType}
            itemColumns={itemColumns}
            modelId={modelId}
            parent={parent}
            onUpdate={generateUpdater(predicate.id)}
            onRemove={() =>
              onUpdate((current) =>
                removeSubconditionMutative(current, predicate.id),
              )
            }
            onToggleGroup={() =>
              onUpdate((current) => {
                const existing = current.subconditions?.[0];
                if (!isAndOrCondition(existing)) {
                  return;
                }
                current.subconditions = [
                  {
                    ...existing,
                    type:
                      existing.type === ConditionType.And
                        ? ConditionType.Or
                        : ConditionType.And,
                  },
                ];
              })
            }
          />
        ))}
        <DetailButton
          isDisabled={isDisabled || itemColumns.length === 0}
          icon={AddBoxIcon}
          size="sm"
          onClick={addPredicate}
        >
          {itemColumns.length === 0
            ? "Add item fields on the event column"
            : "Add item condition"}
        </DetailButton>
      </Column>
    </QueryBuilderContext.Provider>
  );
};

const ItemPredicate = ({
  condition,
  isDisabled,
  isRealtimeEntryEvent,
  isFirst,
  groupType,
  itemColumns,
  modelId,
  parent,
  onUpdate,
  onRemove,
  onToggleGroup,
}: {
  condition: PropertyCondition;
  isDisabled?: boolean;
  isRealtimeEntryEvent: boolean;
  isFirst: boolean;
  groupType: ConditionType.And | ConditionType.Or;
  itemColumns: { relativePath: string; propertyType: ColumnType }[];
  modelId: string;
  parent: ObjectArrayItemFiltersProps["parent"];
  onUpdate: (updater: (condition: PropertyCondition) => void) => void;
  onRemove: () => void;
  onToggleGroup: () => void;
}) => {
  const { propertyError, operatorError, valueError } =
    usePropertyFilterValidation(condition, {
      isRealtimeAudience: true,
      isRealtimeEntryEventArrayItem: isRealtimeEntryEvent,
    });
  const selected = getPropertyNameFromColumn(condition.property) ?? "";

  return (
    <Row gap={2} alignItems="flex-start">
      {isFirst ? null : (
        <AndOrToggleButton
          isDisabled={isDisabled}
          conditionType={groupType}
          onClick={onToggleGroup}
        />
      )}
      <Column>
        <Select
          aria-label="Item field"
          isDisabled={isDisabled}
          isInvalid={Boolean(propertyError)}
          options={itemColumns.map((column) => ({
            label: column.relativePath,
            value: column.relativePath,
          }))}
          value={selected}
          width="auto"
          onChange={(relativePath) => {
            const field = itemColumns.find(
              (column) => column.relativePath === relativePath,
            );
            if (!field || !relativePath) {
              return;
            }
            onUpdate((current) => {
              current.property = {
                type: "raw",
                name: relativePath,
                modelId,
              };
              current.propertyType = field.propertyType;
              current.operator =
                isRealtimeEntryEvent &&
                field.propertyType === ColumnType.Timestamp
                  ? TimestampOperator.Before
                  : DefaultOperators[field.propertyType];
              if (
                isRealtimeEntryEvent &&
                field.propertyType === ColumnType.Timestamp
              ) {
                current.timeType = TimeType.Absolute;
              }
              current.value = null;
            });
          }}
        />
        {propertyError && <ErrorMessage>{propertyError}</ErrorMessage>}
      </Column>
      <PropertyOperatorValueFilter
        condition={condition}
        isDisabled={isDisabled}
        loadingSuggestions={false}
        operatorError={operatorError}
        parent={parent}
        suggestions={[]}
        allowPropertyComparison={false}
        valueError={valueError}
        onUpdate={onUpdate}
      />
      <XButton isDisabled={isDisabled} onRemove={onRemove} />
    </Row>
  );
};
